import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BodyLink,
  Message,
  NextIcon,
  Spacer,
  Spinner
} from '@kvdbil/components';
import { formatMoney } from '~/helpers/currency';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import SelectWithControl from '../../../../../../../../shared/components/Fields/SelectWithControl';
import { useForm } from 'react-hook-form';
import PriceTextFieldWithControl from '../../../../../../../../shared/components/Fields/PriceTextFieldWithControl';
import { SelectOption } from '~/App/shared/interfaces/SelectOption';
import { getReleasingCost } from '~/helpers/orchestration/releasing';
import { requestErrorHandler } from '~/helpers/notifyError';
import { ReleasingObjectInfo } from './ReleasingService';
import { convertScandinavianMilesToKilometers } from '~/helpers/odometer';
import Link from '~/App/shared/components/Link';

const Container = styled.div`
  display: grid;
  grid-row-gap: 0.75rem;
`;

const ReleasingRadioButtonContainer = styled.div`
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light3};
  padding: 0.3em;
`;

const ReleasingRadioButton = styled.button<{ isActive: boolean }>`
  width: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  height: 3em;
  background: ${({ theme }) => theme.colors.background.light};
  font-size: 0.9rem;
  border-radius: 3px;
  ${({ isActive, theme }) =>
    isActive &&
    `
    background: ${theme.colors.secondary.main};
    color: ${theme.colors.text.light};
    font-weight: bold;
  `}
`;

const SpinnerContainer = styled.div`
  margin: 1em 0;
  display: flex;
  justify-content: center;
`;

const ReleasingAmount = styled.div`
  font-weight: 500;
  font-size: 1.125em;
  text-align: center;
  padding: 1em 0;
  display: block;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const periodOptions = [
  { value: '12', label: 12 },
  { value: '24', label: 24 },
  { value: '36', label: 36 }
];

const createDistanceOptions = () => {
  const distanceArray: SelectOption[] = [];
  // Minimum & maximum distance (Swedish miles)
  const minimumDistance = 1000;
  const maximumDistance = 3500;

  for (let d = minimumDistance; d <= maximumDistance; d += 500) {
    const obj = {
      value: String(convertScandinavianMilesToKilometers(d)),
      label: String(d)
    };

    distanceArray.push(obj);
  }

  return distanceArray;
};

const distanceOptions = createDistanceOptions();

interface Props {
  objectInfo: ReleasingObjectInfo;
  buttonText: string;
  isBuyNow: boolean;
}

const LeasingCalculatorForm = ({ objectInfo, buttonText, isBuyNow }: Props) => {
  const { t } = useTranslation();
  const [privateLeasing, setIsPrivateLeasing] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [monthlyCostStatus, setMonthlyCostStatus] = useState<string | null>(
    null
  );
  const [releaseCost, setReleaseCost] = useState<number | null>(null);

  // eslint-disable-next-line no-undef
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { control, getValues, watch } = useForm({
    defaultValues: {
      price: objectInfo.price ?? 0,
      drivingDistance: { label: '1000', value: '10000' },
      leasingPeriod: { label: '12', value: '12' }
    }
  });

  const { price } = getValues();

  const handleErrorMessage = useCallback(
    (message: string[]) => {
      const illegalCombination =
        message.indexOf('Illegal period/km combination') !== -1;
      const nonValidRegNr = message.indexOf('Could not find regno') !== -1;

      if (illegalCombination) {
        return t(
          'Releasing is not available with this combination of distance & leasing period'
        );
      } else if (nonValidRegNr) {
        return t('Releasing is not available for this vehicle');
      }
      return t('Something went wrong, please try again later.');
    },
    [t]
  );

  const fetchReleasingCost = useCallback(
    async (regPlate: string) => {
      setLoading(true);

      const values = getValues();
      const { price, drivingDistance, leasingPeriod } = values;

      const { status, data } = await getReleasingCost({
        regPlate: regPlate,
        kmPerYear: drivingDistance.value,
        leasingPeriodMonths: leasingPeriod.value,
        isPrivate: privateLeasing,
        price: Number(price) ?? 0
      });

      let releaseSum: null | number = null,
        errorMessage: string | null = null,
        validationMessage: string | null = null,
        monthlyCostStatus: string | null = null;

      if (price) {
        if (data && data?.monthlyCostStatus === 'INVALID') {
          releaseSum = data.monthlyCost;
          validationMessage = t('Try a higher price');
          monthlyCostStatus = 'INVALID';
        } else if (status === 200) {
          releaseSum = data.monthlyCost;
          monthlyCostStatus = data.monthlyCostStatus;
        } else if (status === 500) {
          releaseSum = data.monthlyCost;
          validationMessage = t('Releasing available');
          monthlyCostStatus = 'INVALID';
        } else {
          errorMessage = handleErrorMessage(data?.message ?? []);
        }

        setLoading(false);
        setErrorMessage(errorMessage);
        setMonthlyCostStatus(monthlyCostStatus);
        setValidationMessage(validationMessage);
        setReleaseCost(releaseSum);
        objectInfo.updateHOC(releaseSum);

        objectInfo.setReleasingInformation &&
          objectInfo.setReleasingInformation({
            selectedDistance: drivingDistance.value,
            selectedPeriod: leasingPeriod.value,
            price: price,
            isPrivate: privateLeasing,
            sum: releaseSum
          });
      }
    },
    [getValues, objectInfo, privateLeasing, handleErrorMessage, t]
  );

  const handleInputChange = useCallback(() => {
    const { price, drivingDistance, leasingPeriod } = getValues();

    if (
      drivingDistance.value &&
      leasingPeriod.value &&
      price &&
      objectInfo.registrationPlate
    ) {
      if (Number(price) > 0) {
        // Reduce amount of requests
        timeoutRef && timeoutRef.current && clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          fetchReleasingCost(objectInfo.registrationPlate).catch(
            requestErrorHandler
          );
        }, 500);
      }
    }
  }, [objectInfo.registrationPlate, fetchReleasingCost, getValues]);

  const priceWatch = watch('price');
  const distanceWatch = watch('drivingDistance').value;
  const leasingWatch = watch('leasingPeriod').value;

  useEffect(() => {
    handleInputChange();
  }, [
    priceWatch,
    distanceWatch,
    leasingWatch,
    privateLeasing,
    handleInputChange
  ]);

  return (
    <div>
      <Container>
        <ReleasingRadioButtonContainer>
          <ReleasingRadioButton
            isActive={privateLeasing}
            onClick={() => setIsPrivateLeasing(true)}
          >
            {t('Private leasing')}
          </ReleasingRadioButton>
          <ReleasingRadioButton
            isActive={!privateLeasing}
            onClick={() => setIsPrivateLeasing(false)}
          >
            {t('Company leasing')}
          </ReleasingRadioButton>
        </ReleasingRadioButtonContainer>
        <PriceTextFieldWithControl
          control={control}
          name="price"
          label={t('Enter price')}
          disabled={isBuyNow}
        />

        <SelectWithControl
          control={control}
          isSearchable={false}
          name="drivingDistance"
          options={distanceOptions}
          label={t('Yearly driving distance')}
        />

        <SelectWithControl
          control={control}
          isSearchable={false}
          name="leasingPeriod"
          options={periodOptions}
          label={t('Leasing period')}
        />

        {!isLoading && errorMessage && (
          <>
            <Message type="error" fullWidth>
              {errorMessage}
            </Message>
            <Spacer height={0} />
          </>
        )}
      </Container>

      {isLoading && (
        <SpinnerContainer>
          <Spinner color="neutral" speed="fast" />
        </SpinnerContainer>
      )}

      {!isLoading &&
        price &&
        Boolean(Number(price)) &&
        monthlyCostStatus === 'VALID' && (
          <ReleasingAmount>
            <span>{`${t('Monthly cost')}: `}</span>
            <span>
              {formatMoney({
                value: releaseCost ?? 0,
                currency: privateLeasing
                  ? t('SEK/month')
                  : t('SEK/month excl VAT')
              })}
            </span>
          </ReleasingAmount>
        )}
      {!isLoading && validationMessage && (
        <ReleasingAmount>{validationMessage}</ReleasingAmount>
      )}

      <StyledLink to={'/ROUTES.LEASING_PAGE'}>
        <BodyLink as="span">{buttonText}</BodyLink>
        <NextIcon />
      </StyledLink>
    </div>
  );
};

export default LeasingCalculatorForm;
